import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useParams, useNavigate } from 'react-router-dom';
import { staffingRequirementsSchema, StaffingRequirementsFormValues } from '../../lib/validations/teamSchema';
import { useTeamStore } from '../../lib/store/teamStore';
import Alert from '../common/Alert';
import Button from '../common/Button';
import Card from '../common/Card';
import Input from '../common/Input';

export const TeamStaffingRequirementsForm = () => {
  const { teamId } = useParams<{ teamId: string }>();
  const navigate = useNavigate();
  const { currentTeam, isLoading, error, setCurrentTeam, updateTeam } = useTeamStore();
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  const { register, handleSubmit, formState: { errors }, reset } = useForm<StaffingRequirementsFormValues>({
    resolver: zodResolver(staffingRequirementsSchema),
    defaultValues: {
      shifts_per_provider: 13,
      day_providers_required: 2,
      night_providers_required: 1
    }
  });
  
  useEffect(() => {
    if (teamId) {
      setCurrentTeam(teamId);
    }
  }, [teamId, setCurrentTeam]);
  
  useEffect(() => {
    if (currentTeam) {
      reset({
        shifts_per_provider: currentTeam.shifts_per_provider,
        day_providers_required: currentTeam.day_providers_required,
        night_providers_required: currentTeam.night_providers_required
      });
    }
  }, [currentTeam, reset]);
  
  const onSubmit = async (data: StaffingRequirementsFormValues) => {
    if (!teamId) return;
    
    setIsSubmitting(true);
    setUpdateError(null);
    setSuccessMessage(null);
    
    try {
      await updateTeam(teamId, data);
      setSuccessMessage('Staffing requirements updated successfully');
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
    } catch (err) {
      setUpdateError(err instanceof Error ? err.message : 'Failed to update staffing requirements. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin h-8 w-8 border-t-2 border-b-2 border-primary rounded-full"></div>
      </div>
    );
  }
  
  if (error) {
    return (
      <Alert variant="error" className="mb-4">
        {error}
      </Alert>
    );
  }
  
  if (!currentTeam) {
    return (
      <Alert variant="error" className="mb-4">
        Team not found
      </Alert>
    );
  }
  
  return (
    <Card className="max-w-2xl mx-auto p-6">
      <h2 className="text-2xl font-semibold mb-6">Staffing Requirements</h2>
      
      {updateError && (
        <Alert variant="error" className="mb-4">
          {updateError}
        </Alert>
      )}
      
      {successMessage && (
        <Alert variant="success" className="mb-4">
          {successMessage}
        </Alert>
      )}
      
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="space-y-4">
          <Input
            type="number"
            label="Shifts Per Provider"
            {...register('shifts_per_provider', { valueAsNumber: true })}
            error={errors.shifts_per_provider?.message}
            helperText="Target number of shifts per provider per scheduling period"
            required
          />
          
          <Input
            type="number"
            label="Day Providers Required"
            {...register('day_providers_required', { valueAsNumber: true })}
            error={errors.day_providers_required?.message}
            helperText="Number of providers required for each day shift"
            required
          />
          
          <Input
            type="number"
            label="Night Providers Required"
            {...register('night_providers_required', { valueAsNumber: true })}
            error={errors.night_providers_required?.message}
            helperText="Number of providers required for each night shift"
            required
          />
        </div>
        
        <div className="flex justify-end space-x-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(`/teams/${teamId}/settings`)}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            isLoading={isSubmitting}
          >
            Save Requirements
          </Button>
        </div>
      </form>
    </Card>
  );
}; 